import { Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setSubLoading } from '../../Redux/Services/AuthSlice';
import { NotificationSound } from "../CommonComponents/CommonNotification";
import { ApiCall, deleteData, encryptPassword } from "../CommonComponents/CustomHooks";
import { initializeFilterData } from "../CommonComponents/EnvConfig";
import { handleDownloadExcel } from "../CommonComponents/ExportData";
import { FormButton, InputBox, Switch } from "../CommonComponents/InputBox";
import { Table } from "../CommonComponents/Table";

const ChargeTypeMaster = () => {
    const inputrefs = useRef([])
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([])
    const [currentPageNo, setCurrentPageNo] = useState(1);
    const [filterData, setFilterData] = useState(initializeFilterData)
    const [sortConfig, setSortConfig] = useState({ SortColumn: null, SortOrder: null });
    const initialFormData = {
        ChargeTypeID: '',
        chargeTypeCode: 'System Generated',
        chargeTypeName: '',
        hsnCode: '',
        isTaxApplicable: false,
        isFuelApplicable: false,
        active: true
    }

    const [formData, setFormData] = useState(initialFormData);

    const GetChargeTypeMaster = async (CurrentPage) => {
        let PageIndex = 1
        if (CurrentPage !== undefined && typeof (CurrentPage) !== "object") {
            PageIndex = CurrentPage;
            setCurrentPageNo(CurrentPage);
        }
        else {
            setCurrentPageNo(1);
        }
        const data = {
            ...filterData,
            Type: 'S',
            PageIndex: `${PageIndex}`,
            ...sortConfig
        }
        setIsLoading(true)
        let res = await ApiCall(`/api/Master/ShowChargeTypeMaster`, data)
        if (res.Status === "SUCCESS") {
            setTableData(res)
        }
        if (res.Status === "ERROR") {
            setTableData([])
        }
        setIsLoading(false)
        if (res.Status === 'EXPIRED' || res.Status === 'UNAUTHORIZED') {
            await NotificationSound(res.Status, res.Message, res?.focus, inputrefs);
        }
    }

    const handleClear = () => {
        setFormData({
            ...initialFormData,
        });
        filterData.SearchText = '';
        inputrefs.current['txtChargeTypeName'].focus()
        GetChargeTypeMaster();
    }

    const handleSubmit = async () => {
        await dispatch(setSubLoading(true))
        let data = await ApiCall(`/api/Master/InsertUpdateChargeTypeMaster`, { ...formData, password: encryptPassword(formData.password) })
        await dispatch(setSubLoading(false))
        await NotificationSound(data.status, data.message, data?.focus, inputrefs, handleClear);
    };

    const handleEditData = async (id) => {
        await dispatch(setSubLoading(true))
        let editData = await ApiCall(`/api/Master/EditChargeTypeMaster`, { ChargeTypeID: id })
        await dispatch(setSubLoading(false))
        if (editData.status === 'SUCCESS') {
            setFormData((pre) => ({
                ...pre, ...editData,
            }))
            await inputrefs.current[editData?.focus].focus();
        } else {
            await NotificationSound(editData.status, editData.message, editData?.focus, inputrefs);
        }
    }

    const handleDelete = async (id) => {
        await dispatch(setSubLoading(true))
        await deleteData(`/api/Master/DeleteChargeTypeMaster`, { ChargeTypeID: id }, GetChargeTypeMaster, handleClear, '', inputrefs)
        await dispatch(setSubLoading(false))
    }

    const handleChange1 = (i, p) => {
        GetChargeTypeMaster(p)
    };

    const handleSorting = (SortColumn) => {
        let SortOrder = 'ASC';
        if (sortConfig && sortConfig.SortColumn === SortColumn && sortConfig.SortOrder === 'ASC') {
            SortOrder = 'DESC';
        }
        setSortConfig({ SortColumn, SortOrder });
    };

    useEffect(() => {
        document.title = `Charge Type Master`
        inputrefs.current['txtChargeTypeName'].focus()
    }, [])

    useEffect(() => {
        GetChargeTypeMaster();
    }, [sortConfig]);

    return (
        <>
            <div className='bg-secondary-50 shadow-lg rounded-md p-3'>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary-600" />
                        <h2 className="text-xl font-semibold text-primary-600">Charge Type Master</h2>
                    </div>
                </div>

                <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-3">
                    <InputBox
                        id="txtChargeTypeCode"
                        label="Charge Type Code"
                        disabled
                        placeholder="Enter Charge Type Code"
                        value={formData.chargeTypeCode}
                        onChange={(e) =>
                            setFormData({ ...formData, chargeTypeCode: e.target.value })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtChargeTypeName'].focus();
                            }
                        }}
                    />


                    <InputBox
                        id="txtChargeTypeName"
                        label="Charge Type Name"
                        placeholder="Enter Charge Type Name"
                        value={formData.chargeTypeName}
                        onChange={(e) =>
                            setFormData({ ...formData, chargeTypeName: e.target.value })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtHSNCode'].focus();
                            }
                        }}
                        required
                    />


                    <InputBox
                        id="txtHSNCode"
                        label="HSN Code"
                        placeholder="Enter HSN Code"
                        value={formData.hsnCode}
                        onChange={(e) =>
                            setFormData({ ...formData, hsnCode: e.target.value })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtIsTaxApplicable'].focus();
                            }
                        }}
                    />

                    <Switch
                        id="txtIsTaxApplicable"
                        label="Tax Applicable"
                        checked={formData.isTaxApplicable}
                        onChange={(e) =>
                            setFormData({ ...formData, isTaxApplicable: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtIsFuelApplicable'].focus();
                            }
                        }}
                        divclassname="mt-2"
                    />

                    <Switch
                        id="txtIsFuelApplicable"
                        label="Fuel Applicable"
                        checked={formData.isFuelApplicable}
                        onChange={(e) =>
                            setFormData({ ...formData, isFuelApplicable: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['txtActive'].focus();
                            }
                        }}
                    />

                    <Switch
                        id="txtActive"
                        label="Active"
                        checked={formData.active}
                        onChange={(e) =>
                            setFormData({ ...formData, active: e.target.checked })
                        }
                        inputrefs={inputrefs}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                inputrefs.current['btnSub'].focus();
                            }
                        }}
                    />

                </div>

                <div className='card-body'>
                    <FormButton
                        id={'btnSub'}
                        inputrefs={inputrefs}
                        handleSubmit={handleSubmit}
                        handleExport={async () => {
                            await dispatch(setSubLoading(true))
                            handleDownloadExcel(`/api/Master/ShowChargeTypeMaster`, filterData, 'Charge Type Master')
                            await dispatch(setSubLoading(false))
                        }}
                        handleClear={handleClear}
                    />
                </div>


            </div>
            <div className="mt-3">
                <Table
                    handleEdit={handleEditData}
                    handleDelete={handleDelete}
                    loading={isLoading}
                    PageName={'ChargeTypeMaster'}
                    handleFilter={() => {
                        GetChargeTypeMaster()
                        setFilterData({
                            ...filterData,
                        })
                    }}
                    filterData={filterData}
                    tableData={tableData}
                    setFilterData={setFilterData}
                    currentPageNo={currentPageNo}
                    handleChange1={handleChange1}
                    sortConfig={sortConfig}
                    handleSorting={handleSorting}
                />
            </div>
        </>
    )
}
export default ChargeTypeMaster;